import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Calendar, Filter, Download, BarChart2, Image } from 'lucide-react';
import { CHART_COLORS } from '../../utils/chartTheme';
import { GroupedBarChart } from '../../utils/chartComponents';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { users } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import DateRangePicker from '../UI/DateRangePicker';
import Avatar from '../UI/Avatar';
import { Autocomplete } from '@anyemp/ui-kit';
import StatisticsTable from './StatisticsTable';

const PeriodComparisonPortraitStats = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [period1, setPeriod1] = useState({ startDate: '', endDate: '' });
  const [period2, setPeriod2] = useState({ startDate: '', endDate: '' });
  const [filters, setFilters] = useState({ manager_ids: [] });
  const [managers, setManagers] = useState([]);

  const QUICK_PRESETS = [
    { label: 'This Month vs Last Month', value: 'month_to_month' },
    { label: 'This Quarter vs Last Quarter', value: 'quarter_to_quarter' },
    { label: 'This Year vs Last Year', value: 'year_to_year' }
  ];

  const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    const now = new Date();
    setPeriod1({ startDate: formatDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)), endDate: formatDate(new Date(now.getFullYear(), now.getMonth(), 0)) });
    setPeriod2({ startDate: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)) });
    fetchManagers();
  }, []);

  const fetchManagers = async () => {
    try {
      const res = await users.getAllUsers();
      const arr = res.data?.users || res.users || res.data || res;
      setManagers((Array.isArray(arr) ? arr : []).map(u => ({ value: String(u.id), label: u.fullName || u.name || u.email })));
    } catch (e) { console.error('Error loading managers:', e); }
  };

  const applyQuickPreset = (preset) => {
    const now = new Date();
    if (preset === 'month_to_month') {
      setPeriod1({ startDate: formatDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)), endDate: formatDate(new Date(now.getFullYear(), now.getMonth(), 0)) });
      setPeriod2({ startDate: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)) });
    } else if (preset === 'quarter_to_quarter') {
      const cq = Math.floor(now.getMonth() / 3);
      const lq = cq - 1;
      const lqYear = lq < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lqMonth = lq < 0 ? 9 : lq * 3;
      setPeriod1({ startDate: formatDate(new Date(lqYear, lqMonth, 1)), endDate: formatDate(new Date(lqYear, lqMonth + 3, 0)) });
      setPeriod2({ startDate: formatDate(new Date(now.getFullYear(), cq * 3, 1)), endDate: formatDate(new Date(now.getFullYear(), (cq + 1) * 3, 0)) });
    } else if (preset === 'year_to_year') {
      setPeriod1({ startDate: formatDate(new Date(now.getFullYear() - 1, 0, 1)), endDate: formatDate(new Date(now.getFullYear() - 1, 11, 31)) });
      setPeriod2({ startDate: formatDate(new Date(now.getFullYear(), 0, 1)), endDate: formatDate(new Date(now.getFullYear(), 11, 31)) });
    }
  };

  const runComparison = async () => {
    if (!period1.startDate || !period1.endDate || !period2.startDate || !period2.endDate) {
      toast.error('Please select both periods'); return;
    }
    try {
      setLoading(true); setError(null);
      const response = await statisticsService.getPeriodComparisonPortrait({
        period1_start: period1.startDate, period1_end: period1.endDate,
        period2_start: period2.startDate, period2_end: period2.endDate,
        manager_ids: filters.manager_ids.length ? filters.manager_ids : undefined
      });
      setComparisonData(response.data || response);
    } catch (err) {
      setError(err.message);
      toast.error('Failed to run comparison');
    } finally {
      setLoading(false);
    }
  };

  const renderChangeIndicator = (change) => {
    const { absolute, percentage } = change;
    const isPositive = absolute > 0;
    const isNegative = absolute < 0;
    const IconComponent = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
    const colorClass = isPositive ? 'text-green-600 dark:text-green-400' : isNegative ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400';
    return (
      <div className={`flex items-center gap-1 ${colorClass}`}>
        <IconComponent className="h-4 w-4" />
        <span className="font-semibold">{absolute >= 0 ? '+' : ''}{absolute}</span>
        <span className="text-sm">({percentage >= 0 ? '+' : ''}{percentage}%)</span>
      </div>
    );
  };

  const renderSummaryCards = () => {
    if (!comparisonData) return null;
    const { period1: p1, period2: p2, comparison } = comparisonData;
    return (
      <div className="space-y-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Portraits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Total Portraits</h3>
            <div className="flex items-baseline justify-between mb-2">
              <span className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">{p2.total_portraits}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">vs {p1.total_portraits}</span>
            </div>
            {renderChangeIndicator(comparison.total_portraits)}
          </div>
        </div>
      </div>
    );
  };

  const renderComparisonChart = () => {
    if (!comparisonData) return null;
    const { period1: p1, period2: p2 } = comparisonData;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Portrait Uploads Comparison</h3>
        <GroupedBarChart
          data={[{ name: 'Total Portraits', [p1.label]: p1.total_portraits || 0, [p2.label]: p2.total_portraits || 0 }]}
          bars={[{ key: p1.label, name: p1.label, color: CHART_COLORS[0] }, { key: p2.label, name: p2.label, color: CHART_COLORS[1] }]}
        />
      </div>
    );
  };

  const rowKeyManager = useCallback(
    (item) => item.manager?.id || item.manager?.email || JSON.stringify(item),
    []
  );

  const tableColumns = useMemo(() => {
    if (!comparisonData) return [];
    const { period1: p1, period2: p2 } = comparisonData;
    return [
      {
        label: 'Manager', key: 'manager', sortable: true,
        render: (item) => {
          const manager = item.manager;
          if (!manager) return <span className="text-gray-400 dark:text-gray-500">Unassigned</span>;
          return (
            <div className="flex flex-wrap items-center gap-2">
              <Avatar avatar={manager.avatar} name={manager.fullName || manager.name || manager.email} size="sm" />
              <span className="text-gray-900 dark:text-gray-100">{manager.fullName || manager.name || manager.email}</span>
            </div>
          );
        }
      },
      { label: p1.label, key: 'period1_total', sortable: true, render: (item) => { const m = p1.by_manager?.find(m => m.manager_id === item.manager_id); return <span className="font-medium text-gray-900 dark:text-gray-100">{m?.total || 0}</span>; } },
      { label: p2.label, key: 'period2_total', sortable: true, render: (item) => { const m = p2.by_manager?.find(m => m.manager_id === item.manager_id); return <span className="font-medium text-gray-900 dark:text-gray-100">{m?.total || 0}</span>; } },
      { label: 'Change', key: 'change', sortable: true, sortValue: (item) => item.total.absolute, render: (item) => renderChangeIndicator(item.total) }
    ];
  }, [comparisonData]);

  const handleExport = () => {
    if (!comparisonData) return;
    const { period1: p1, period2: p2, comparison } = comparisonData;
    const headers = ['Manager', p1.label, p2.label, 'Change (Absolute)', 'Change (%)'];
    const rows = comparison.by_manager.map(item => {
      const manager = item.manager;
      const name = manager ? (manager.fullName || manager.name || manager.email) : 'Unassigned';
      const m1 = p1.by_manager?.find(m => m.manager_id === item.manager_id);
      const m2 = p2.by_manager?.find(m => m.manager_id === item.manager_id);
      return [name, m1?.total || 0, m2?.total || 0, item.total.absolute, `${item.total.percentage}%`];
    });
    const blob = new Blob(['\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `period-comparison-portrait-${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Image className="h-6 w-6 text-purple-500" />
            <h1 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">Period Comparison — Portraits</h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Compare portrait uploads across two time periods</p>
        </div>
        {comparisonData && (
          <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors">
            <Download className="h-4 w-4" /><span>Export CSV</span>
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Quick Presets</label>
          <div className="flex flex-wrap gap-2">
            {QUICK_PRESETS.map(p => (
              <button key={p.value} onClick={() => applyQuickPreset(p.value)} className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">{p.label}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <DateRangePicker label="Period 1" startDate={period1.startDate} endDate={period1.endDate} onChange={(r) => setPeriod1(r)} />
          <DateRangePicker label="Period 2" startDate={period2.startDate} endDate={period2.endDate} onChange={(r) => setPeriod2(r)} />
        </div>

        <button onClick={() => setFiltersExpanded(!filtersExpanded)} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4 transition-colors">
          <Filter className="h-4 w-4" /><span>{filtersExpanded ? 'Hide' : 'Show'} Filters</span>
        </button>

        {filtersExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Managers</label>
              <Autocomplete multiple clearable fullWidth options={managers} value={filters.manager_ids} onChange={(v) => setFilters(prev => ({ ...prev, manager_ids: v || [] }))} placeholder="All managers" />
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={runComparison} disabled={loading} className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium">
            <BarChart2 className="h-4 w-4" />{loading ? 'Running...' : 'Compare Periods'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">{error}</div>}

      {comparisonData && (
        <>
          {renderSummaryCards()}
          {renderComparisonChart()}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Comparison by Manager</h3>
            <StatisticsTable
              data={comparisonData.comparison.by_manager}
              columns={tableColumns}
              isLoading={loading}
              rowKey={rowKeyManager}
            />
          </div>
        </>
      )}

      {loading && !comparisonData && <div className="flex justify-center items-center min-h-[200px]"><Loader variant="component" size="md" /></div>}

      {!comparisonData && !loading && !error && (
        <div className="flex flex-col items-center justify-center min-h-[300px] text-gray-400 dark:text-gray-500">
          <Calendar className="h-12 w-12 mb-3 opacity-50" />
          <p className="text-lg font-medium">Select periods and run comparison</p>
          <p className="text-sm mt-1">Choose two time periods to compare portrait upload statistics</p>
        </div>
      )}
    </div>
  );
};

export default PeriodComparisonPortraitStats;
